// ════════════════════════════════════════════════════════════════════════
//  Die Lehrgänge selbst
//
//  Bis hierher gab es genau einen, und sein Slug stand als Konstante im Code.
//  `curricula` war schon immer eine Tabelle; jetzt wird sie auch als solche
//  gepflegt.
// ════════════════════════════════════════════════════════════════════════

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parseBundle, type Bundle } from "@otto/lehrgang";
import type { Json, OttoClient } from "@otto/plattform";
import { keysOf } from "../../../shared/lib/contentKeys.js";

export type Curriculum = {
  readonly id: string;
  readonly slug: string;
  readonly title: string;
  readonly subtitle: string | null;
  readonly position: number;
  readonly requiresId: string | null;
  readonly retiredAt: string | null;
  /** Höchste veröffentlichte Fassung, oder null. */
  readonly publishedVersion: number | null;
  readonly hasDraft: boolean;
  /**
   * Wer den offenen Entwurf angelegt hat, oder null.
   *
   * Kommt aus derselben Abfrage und wird nirgends angezeigt: die Führung durch
   * die ersten Schritte braucht die Angabe, um ihre eine gemessene Station an
   * der EIGENEN Spur abzuhaken statt an der irgendeiner Kollegin. Eine Zeile
   * „angefangen von …“ auf dem Bildschirm wäre eine eigene Entscheidung.
   */
  readonly draftBy: string | null;
};

const KEY = ["curricula"] as const;

export const CURRICULA_KEY = KEY;

export function useCurricula(client: OttoClient) {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<Curriculum[]> => {
      const { data, error } = await client
        .from("curricula")
        .select(
          "id, slug, title, subtitle, position, requires_id, retired_at, curriculum_versions(version, published_at, created_by)",
        )
        .order("position");
      if (error) throw error;

      return (data ?? []).map((row) => {
        const versions = row.curriculum_versions as {
          version: number;
          published_at: string | null;
          created_by: string | null;
        }[];
        const published = versions
          .filter((v) => v.published_at !== null)
          .map((v) => v.version);
        return {
          id: row.id,
          slug: row.slug,
          title: row.title,
          subtitle: row.subtitle,
          position: row.position,
          requiresId: row.requires_id,
          retiredAt: row.retired_at,
          publishedVersion: published.length > 0 ? Math.max(...published) : null,
          hasDraft: versions.some((v) => v.published_at === null),
          // Höchstens einer je Lehrgang – das hält `curriculum_versions_one_draft`
          // in der Datenbank fest, nicht diese Zeile.
          draftBy: versions.find((v) => v.published_at === null)?.created_by ?? null,
        };
      });
    },
  });
}

/** Der Lehrgang aus der Adresse. Undefined, solange geladen wird; null, wenn es ihn nicht gibt. */
export function curriculumBySlug(
  list: readonly Curriculum[] | undefined,
  slug: string,
): Curriculum | null | undefined {
  if (list === undefined) return undefined;
  return list.find((c) => c.slug === slug) ?? null;
}

/**
 * Neuer Lehrgang – samt seinem ersten Entwurf, in einem Vorgang.
 *
 * Beides zusammen, weil ein Lehrgang ohne Fassung nichts ist, woran jemand
 * arbeiten könnte: die Liste zeigte einen Namen, und dahinter läge eine leere
 * Seite mit einem Knopf. Scheitert der zweite Schritt, wird der erste
 * zurückgenommen.
 */
export function useCreateCurriculum(client: OttoClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      slug: string;
      title: string;
      subtitle: string | null;
      requiresId: string | null;
      position: number;
      bundle: Bundle;
    }) => {
      const { data, error } = await client
        .from("curricula")
        .insert({
          slug: input.slug,
          title: input.title,
          subtitle: input.subtitle,
          requires_id: input.requiresId,
          position: input.position,
        })
        .select("id, slug")
        .single();
      if (error) throw error;

      const { error: versionError } = await client.from("curriculum_versions").insert({
        curriculum_id: data.id,
        version: 1,
        content: input.bundle as unknown as Json,
        notes: "",
      });
      if (versionError) {
        await client.from("curricula").delete().eq("id", data.id);
        throw versionError;
      }
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

type CurriculumPatch = {
  title?: string;
  subtitle?: string | null;
  position?: number;
  requires_id?: string | null;
  retired_at?: string | null;
};

export function useUpdateCurriculum(client: OttoClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      title?: string;
      subtitle?: string | null;
      position?: number;
      requiresId?: string | null;
      retiredAt?: string | null;
    }) => {
      // Nur, was wirklich mitkommt: die Stammdaten werden feldweise beim
      // Verlassen des Feldes gespeichert, und ein `undefined` würde eine
      // gefüllte Spalte leeren.
      const patch: CurriculumPatch = {};
      if (input.title !== undefined) patch.title = input.title;
      if (input.subtitle !== undefined) patch.subtitle = input.subtitle;
      if (input.position !== undefined) patch.position = input.position;
      if (input.requiresId !== undefined) patch.requires_id = input.requiresId;
      if (input.retiredAt !== undefined) patch.retired_at = input.retiredAt;

      const { error } = await client.from("curricula").update(patch).eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Löschen geht nur, solange nie veröffentlicht wurde.
 *
 * Danach hängen Klassen und Fortschritt daran; dafür gibt es das Stilllegen.
 * Die Datenbank hält das ohnehin (veröffentlichte Fassungen sind nicht
 * löschbar), aber die Oberfläche soll den Knopf gar nicht erst anbieten.
 */
export function useDeleteCurriculum(client: OttoClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await client.from("curricula").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Alle Lektions- und Stufenschlüssel der ANDEREN Lehrgänge.
 *
 * Gebraucht für die Prüfung: Schlüssel sind über Lehrgänge hinweg eindeutig,
 * weil `progress` keinen Lehrgangsbezug hat.
 *
 * Über ALLE veröffentlichten Fassungen, nicht nur die neueste: an einem
 * Schlüssel aus Fassung 3 hängt Fortschritt, auch wenn Fassung 7 ihn nicht mehr
 * führt. Entwürfe zählen bewusst nicht mit – an ihnen hängt noch nichts, und
 * zwei gleichzeitig offene Entwürfe würden sich sonst gegenseitig blockieren.
 */
export function useOtherCurriculumKeys(client: OttoClient, slug: string) {
  return useQuery({
    queryKey: ["curricula", "keys", slug],
    enabled: slug !== "",
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await client
        .from("curriculum_versions")
        .select("content, curricula!inner(slug)")
        .not("published_at", "is", null);
      if (error) throw error;

      const keys = new Set<string>();
      for (const row of data ?? []) {
        if ((row.curricula as { slug: string }).slug === slug) continue;
        const checked = parseBundle(row.content);
        if ("bundle" in checked) for (const key of keysOf(checked.bundle)) keys.add(key);
      }
      return keys;
    },
  });
}
